import {isEnabled} from '~/src/featureFlags'

/**
 * Unified Route Configuration
 *
 * Routes are now permission-based using modulePermissions from the backend.
 * Each route has a moduleKey that maps to the backend's modulePermissions.
 *
 * The PermissionWrapper checks: modulePermissions[moduleKey].canView === true
 *
 * Available moduleKeys from backend (ModuleKey enum):
 * - dashboard, pre_fill, manage_companies, manage_agencies, manage_orders
 * - service_orders, job_orders, candidates, users_roles, payroll
 * - global, compensation_codes, wc_catalogs, paycodes, authorities, tax, time_off
 * - company_manage_profile, company_manage_orders, company_invoices, company_timesheets
 * - AgencyDocuments (parent) with submodules: ESignFiles, ESignSignatures, ESignTemplates
 * - And submodules like: company_profile, site, bill_codes, etc.
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Layout variant determines which sidebar/header to show
 */
export type LayoutVariant =
  | 'platform'
  | 'main-agency'
  | 'msp'
  | 'agency'
  | 'company'
  | 'general-admin'
  | 'candidate'

/**
 * Route configuration interface
 */
export interface RouteConfig {
  /** Unique route identifier */
  id: string
  /** Route path (relative to layout base) */
  path: string
  /** Component to render - string name that maps to COMPONENT_MAP in AppRoutes */
  component: string
  /** moduleKey from backend modulePermissions - if present, PermissionWrapper checks canView */
  moduleKey?: string
  /** Submodule key if this is a nested permission */
  submoduleKey?: string
  /**
   * Resolve `submoduleKey` strictly: grant only on a node carrying that exact key, and deny when
   * the viewer's tree has none. The default lookup falls back to the `moduleKey` aliases when the
   * key is missing, so an unrelated node answers for it. Set this when a category's catalogue OMITS
   * the key on purpose, so its absence must mean "no". Meaningless without `submoduleKey`.
   */
  strictSubmodule?: boolean
  /** Which layout variants can access this route (if omitted, all variants) */
  variants?: LayoutVariant[]
  /**
   * Only platform administrators (backendRolePermissions.isPlatform) may open this route, whatever
   * their module permissions say — for screens whose API is behind the backend PlatformAdminGuard.
   */
  platformAdminOnly?: boolean
  /** Route metadata */
  meta?: {
    title?: string
    description?: string
    /** Whether this is an index/default route */
    index?: boolean
    /** Redirect path for this route */
    redirectTo?: string
  }
}

/**
 * Layout configuration with routes
 */
export interface LayoutConfig {
  variant: LayoutVariant
  basePath: string
  routes: RouteConfig[]
}

// ============================================================================
// UNIFIED ROUTE LIST
// All routes defined once, filtered by variant and permissions at runtime
// ============================================================================

export const ALL_ROUTES: RouteConfig[] = [
  // ============================================================================
  // DASHBOARD ROUTES - Different component per variant
  // ============================================================================
  {
    id: 'platform-dashboard',
    path: 'dashboard',
    component: 'Dashboard',
    moduleKey: 'dashboard',
    variants: ['platform'],
    meta: {title: 'Dashboard'},
  },
  {
    // main-agency / msp / agency all render the agency dashboard. A mainagencyadmin's permission
    // category is MSP, which resolves to the 'msp' variant once permissions load, while the
    // role-name fallback (used before permissions load) gives it 'main-agency' — so rendering
    // AgencyDashboard for both means the fallback shows the SAME dashboard, with no flash of a
    // separate component. (Replaced the old MspAgencyDashboard, which only ever appeared in that
    // pre-load window and was otherwise a redundant twin.)
    id: 'agency-dashboard',
    path: 'dashboard',
    component: 'AgencyDashboard',
    moduleKey: 'dashboard',
    variants: ['main-agency', 'msp', 'agency'],
    meta: {title: 'Dashboard'},
  },
  {
    id: 'company-dashboard',
    path: 'dashboard',
    component: 'CompanyDashboard',
    moduleKey: 'dashboard',
    variants: ['company'],
    meta: {title: 'Dashboard'},
  },

  // ============================================================================
  // MANAGE COMPANIES ROUTES
  // ============================================================================
  {
    id: 'manage-companies',
    path: 'manage-companies',
    component: 'ManageCompaniesPage',
    moduleKey: 'manage_companies',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-detail',
    path: 'manage-companies/:companyId',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-tab',
    path: 'manage-companies/:companyId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  // Company admin routes - use company_manage_profile permission
  {
    id: 'company-admin-detail',
    path: 'manage-companies/:companyId',
    component: 'CompanyManagementPage',
    moduleKey: 'company_manage_profile',
    variants: ['company'],
  },
  {
    id: 'company-admin-tab',
    path: 'manage-companies/:companyId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'company_manage_profile',
    variants: ['company'],
  },
  {
    id: 'company-create',
    path: 'manage-companies/create/:tab',
    component: 'CompanyDetailPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'create_company',
    variants: ['platform', 'main-agency', 'general-admin', 'agency', 'msp'],
  },
  // Region routes
  {
    id: 'region-create',
    path: 'manage-companies/:companyId/regions/create',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_region',
  },
  {
    id: 'region-detail',
    path: 'manage-companies/:companyId/regions/:regionId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_region',
  },
  // Site routes
  {
    id: 'site-create',
    path: 'manage-companies/:companyId/regions/:regionId/sites/create',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'site',
  },
  {
    id: 'site-detail',
    path: 'manage-companies/:companyId/regions/:regionId/sites/:siteId/:tab',
    component: 'CompanyManagementPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'site',
  },
  // Position routes (under company)
  {
    id: 'company-position-new',
    path: 'manage-companies/:companyId/positions/new',
    component: 'AddNewPositionPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
  },
  {
    id: 'company-position-edit',
    path: 'manage-companies/:companyId/positions/:positionId/edit',
    component: 'EditPositionPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
  },
  {
    id: 'company-position-template',
    path: 'manage-companies/:companyId/positions/templates/new',
    component: 'AddPositionTemplatePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
    variants: ['platform', 'main-agency', 'msp'],
  },
  // Bill code routes
  {
    id: 'company-billcode-add',
    path: 'manage-companies/:companyId/bill-codes/add',
    component: 'AddBillCodePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'bill_codes',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-billcode-edit',
    path: 'manage-companies/:companyId/bill-codes/edit/:billCodeId',
    component: 'AddBillCodePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'bill_codes',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  // Company admin bill code routes (different component)
  {
    id: 'company-admin-billcode-add',
    path: 'manage-companies/:companyId/bill-codes/add',
    component: 'CompanyAdminBillCodePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'bill_codes',
    variants: ['company'],
  },
  {
    id: 'company-admin-billcode-edit',
    path: 'manage-companies/:companyId/bill-codes/edit/:billCodeId',
    component: 'CompanyAdminBillCodePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'bill_codes',
    variants: ['company'],
  },
  // Company role routes (under manage-companies)
  {
    id: 'company-role-add',
    path: 'manage-companies/:companyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-role-edit',
    path: 'manage-companies/:companyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  {
    id: 'company-role-detail',
    path: 'manage-companies/:companyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
  },
  // Company admin role routes
  {
    id: 'company-admin-role-add',
    path: 'manage-companies/:companyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_users_roles',
    variants: ['company'],
  },
  {
    id: 'company-admin-role-edit',
    path: 'manage-companies/:companyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_users_roles',
    variants: ['company'],
  },
  {
    id: 'company-admin-role-detail',
    path: 'manage-companies/:companyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_users_roles',
    variants: ['company'],
  },

  // ============================================================================
  // MANAGE AGENCIES ROUTES
  // ============================================================================
  {
    id: 'manage-agencies',
    path: 'manage-agencies',
    component: 'ManageAgenciesPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'general-admin'],
  },
  {
    id: 'agency-detail',
    path: 'manage-agencies/:agencyId',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  {
    id: 'agency-tab',
    path: 'manage-agencies/:agencyId/:tab',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  // Agency admin routes - use manage_profile permission
  {
    id: 'agency-admin-detail',
    path: 'manage-agencies/:agencyId',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_profile',
    variants: ['agency'],
  },
  {
    id: 'agency-admin-tab',
    path: 'manage-agencies/:agencyId/:tab',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_profile',
    variants: ['agency'],
  },
  {
    /*
     * The staffer performance detail, nested inside Manage Agency > Internal Employees. It renders
     * AgencyManagementPage, so the title row, the branch sidebar and the tab strip stay mounted
     * while the Internal Employees tab shows the detail in place of the list. The segment after
     * :agencyId is the tab's own route (`users-roles`), which keeps that tab selected.
     *
     * Same audience and same strict key as the standalone page (users-roles-staffer-metrics, whose
     * comment has the full reasoning): Platform, MSP and, since PO item 6, Agency holders of
     * `employee_metrics`. The key admits them and nothing else does. `manage_agencies` is the
     * Platform parent of the key. MSP and Agency trees carry it under `manage_profile` instead,
     * which strict mode still finds through its cross-module search. MSP-category roles get the
     * 'msp' layout variant and Agency-category roles 'agency', hence both in the list. A tree
     * without the node, or with it unticked, is denied, and the guard sends it to /unauthorized.
     * There is no build flag: the route ships on.
     *
     * The guard stays this synchronous client check. The page then asks the server
     * (GET /api/team/staffer/access, useStafferAccess; tracker §20.12), whose gate reads every role
     * held, every ancestor and the branch, and refuses with 'You don’t have access to Employee
     * Metrics for this branch.' when it says no, instead of opening on reads that all 403.
     */
    id: 'agency-staffer-detail',
    path: 'manage-agencies/:agencyId/users-roles/employees/:userId',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'employee_metrics',
    strictSubmodule: true,
    variants: ['platform', 'main-agency', 'msp', 'general-admin', 'agency'],
  },
  {
    id: 'agency-catalog-detail',
    path: 'manage-agencies/:agencyId/catalogs/:catalogId/:tab?',
    component: 'CatalogCompensationCodesPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'catalogs',
  },
  {
    id: 'agency-branch-new',
    path: 'manage-agencies/:agencyId/branches/new',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'main-agency', 'general-admin'],
  },
  {
    id: 'agency-branch-detail',
    path: 'manage-agencies/:agencyId/branches/:branchId/:tab?',
    component: 'AgencyManagementPage',
    moduleKey: 'manage_agencies',
  },
  // Agency role routes (under manage-agencies)
  {
    id: 'agency-role-add',
    path: 'manage-agencies/:agencyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'agency_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  {
    id: 'agency-role-edit',
    path: 'manage-agencies/:agencyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'agency_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  {
    id: 'agency-role-detail',
    path: 'manage-agencies/:agencyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'agency_users_roles',
    variants: ['platform', 'main-agency', 'msp', 'general-admin'],
  },
  // Agency admin role routes (for agency variant - their own agency)
  {
    id: 'agency-admin-role-add',
    path: 'manage-agencies/:agencyId/roles/add',
    component: 'AddRolePage',
    moduleKey: 'manage_profile',
    submoduleKey: 'agency_users_roles',
    variants: ['agency'],
  },
  {
    id: 'agency-admin-role-edit',
    path: 'manage-agencies/:agencyId/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'manage_profile',
    submoduleKey: 'agency_users_roles',
    variants: ['agency'],
  },
  {
    id: 'agency-admin-role-detail',
    path: 'manage-agencies/:agencyId/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'manage_profile',
    submoduleKey: 'agency_users_roles',
    variants: ['agency'],
  },
  {
    id: 'agency-new',
    path: 'manage-agencies/new',
    component: 'OnboardingAgency',
    moduleKey: 'manage_agencies',
    variants: ['platform', 'general-admin'],
  },
  // Agency profile redirect (their own profile). The nav links to /manage-agency; this older
  // path is kept for bookmarks. `msp` is here because the layout is picked from the role's
  // category, so mainagencyadmin (MSP category) gets the msp layout, not main-agency.
  {
    id: 'agency-profile-redirect',
    path: 'manage-profile',
    component: 'AgencyProfileRedirect',
    moduleKey: 'manage_profile',
    variants: ['agency', 'msp'],
  },

  // ============================================================================
  // SERVICE ORDER ROUTES
  // ============================================================================
  // Agency/MSP service order routes
  {
    id: 'agency-service-orders',
    path: 'manage-orders',
    component: 'AgencyManageOrdersMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-add',
    path: 'manage-orders/add-order',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-edit',
    path: 'manage-orders/edit-service-order/:serviceOrderId',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-details',
    path: 'manage-orders/:orderId',
    component: 'ServiceOrderDetailsPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-pending',
    path: 'manage-orders/pending',
    component: 'AgencyPendingOrders',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-pending-detail',
    path: 'manage-orders/pending/:serviceOrderId',
    component: 'ServiceOrderDetailsPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-completed',
    path: 'manage-orders/completedOrders',
    component: 'CompletedOrdersMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    // STFFLG-3768: Deleted Orders view. Module gate is canView; the tab and the
    // in-view Restore action are additionally gated on canDelete in-component.
    id: 'agency-service-order-deleted',
    path: 'manage-orders/deleted',
    component: 'DeletedOrdersMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-service-order-summary',
    path: 'manage-orders/:orderId/:type/summary',
    component: 'ServiceOrderSummary',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'company'],
  },
  {
    id: 'agency-candidate-distribution',
    path: 'manage-orders/add-order/candidate-distribution',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },
  {
    id: 'agency-candidate-distribution-position',
    path: 'manage-orders/:serviceOrderId/positions/:positionId/distribute',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'manage_orders',
    submoduleKey: 'service_orders',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
  },

  // Company service order routes (different components)
  {
    id: 'company-service-orders',
    path: 'manage-orders',
    component: 'ManageOrders',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-add',
    path: 'manage-orders/add-order',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-edit',
    path: 'manage-orders/edit-service-order/:serviceOrderId',
    component: 'NewServiceOrderFormPage',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-details',
    path: 'manage-orders/:orderId',
    component: 'OrderDetails',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-service-order-completed',
    path: 'manage-orders/completedOrders',
    component: 'CompanyCompletedOrdersMain',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-candidate-list',
    path: 'manage-orders/candidate-list/:orderId',
    component: 'CompanyCandidateViewMain',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'job_orders',
    variants: ['company'],
  },
  {
    id: 'company-candidate-distribution',
    path: 'manage-orders/add-order/candidate-distribution',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },
  {
    id: 'company-candidate-distribution-edit',
    path: 'manage-orders/edit-service-order/:serviceOrderId/candidate-distribution',
    component: 'CandidateDistributionPageWrapper',
    moduleKey: 'company_manage_orders',
    submoduleKey: 'service_orders',
    variants: ['company'],
  },

  // ============================================================================
  // JOB ORDER ROUTES (Agency only)
  // ============================================================================
  {
    id: 'job-orders',
    path: 'manage-job-orders',
    component: 'MspAgencyJobOrderMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'job-order-details',
    path: 'manage-job-orders/:orderId',
    component: 'MspJobOrdersDetailsMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'job-order-summary',
    path: 'manage-job-orders/summary/:id',
    component: 'JobOrdersDetailsSummaryMain',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'job-order-backup-detail',
    path: 'manage-job-orders/summary/:parentId/backup/:groupId',
    component: 'SubOrderDetailsPage',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'job-order-from-prefill',
    path: 'jobOrderPrefill/:jobOrderId/:prefillId',
    component: 'JobOrderFromPrefill',
    moduleKey: 'manage_orders',
    submoduleKey: 'job_orders',
    variants: ['main-agency', 'msp', 'agency'],
  },

  // ============================================================================
  // PREFILL ROUTES (Agency only)
  // ============================================================================
  {
    id: 'prefill',
    path: 'prefill/:prefillCardId',
    component: 'PrefillMain',
    moduleKey: 'pre_fill',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'prefill-add',
    path: 'prefill-add',
    component: 'AddOrEditPrefill',
    moduleKey: 'pre_fill',
    variants: ['main-agency', 'msp', 'agency'],
  },
  {
    id: 'prefill-edit',
    path: 'prefill-edit',
    component: 'AddOrEditPrefill',
    moduleKey: 'pre_fill',
    variants: ['main-agency', 'msp', 'agency'],
  },

  // ============================================================================
  // CANDIDATES ROUTES
  // ============================================================================
  {
    id: 'candidates',
    path: 'candidates',
    component: 'Candidates',
    moduleKey: 'candidates',
  },

  // ============================================================================
  // REQUESTS ROUTES
  // ============================================================================
  // The centralized Requests page: Time Off requests across the user's scope. The backend
  // (/requests/time-off) enforces the scope and the Requests permission; this gate keeps the link
  // and the page in step with it.
  {
    id: 'requests',
    path: 'requests',
    component: 'RequestsPage',
    moduleKey: 'requests',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
    meta: {title: 'Requests'},
  },

  // ============================================================================
  // USERS & ROLES ROUTES
  // ============================================================================
  {
    id: 'users-roles',
    path: 'users-and-roles',
    component: 'UsersAndRoles',
    moduleKey: 'users_roles',
    variants: ['msp', 'agency', 'company'],
  },
  {
    id: 'platform-general-users',
    path: 'users-and-roles/general-users',
    component: 'GeneralUsers',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  {
    id: 'platform-general-admins',
    path: 'users-and-roles/general-admins',
    component: 'GeneralAdmins',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  {
    id: 'platform-add-admin',
    path: 'users-and-roles/general-admins/add-admin',
    component: 'AddOrEditGa',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  {
    id: 'platform-edit-admin',
    path: 'users-and-roles/general-admins/edit-admin',
    component: 'AddOrEditGa',
    moduleKey: 'users_roles',
    variants: ['platform'],
  },
  // NEW: Users & Roles top navigation page (all views)
  {
    id: 'users-roles-page',
    path: 'users-roles',
    component: 'UsersRolesPage',
    moduleKey: 'users_roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    id: 'users-roles-add-user',
    path: 'users-roles/add-user',
    component: 'AddNewUserPage',
    moduleKey: 'users_roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    id: 'users-roles-edit-user',
    path: 'users-roles/users/:userId/edit',
    component: 'EditUserPage', // Wrapper that fetches user data and renders AddNewUserPage
    moduleKey: 'users_roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    /*
     * The per-staffer performance detail as a standalone page, reached from Users & Roles >
     * Employees. The same detail nested inside Manage Agency > Internal Employees is
     * agency-staffer-detail, held to the same strict key.
     *
     * Platform, MSP and Agency categories (PO item 6, A1; deep-dive staffer-performance-metrics.md
     * §20, which supersedes §9.5b and §9.11 constraint 2). Each is admitted by the
     * `employee_metrics` key and by nothing else. `employee_metrics` is nested under `users_list`
     * rather than beside it, so this route relies on the guard resolving a key at any depth.
     *
     * 'msp' is the layout variant of MSP-category roles and 'agency' of Agency-category roles
     * (getVariantFromCategory, AppRoutes). A category whose variant is missing here has no metrics
     * page in its route table, and View Metrics lands on RouteNotFound. A layout variant is not a
     * role category, and it admits no one: the permission does, and only because the lookup is
     * strict.
     *
     * `users_roles` is a top-level LEAF in every category's catalogue. The key lives deeper
     * (platform: manage_agencies > agency_users_roles > users_list; MSP and Agency: manage_profile >
     * …). The Agency catalogue carries the key, and a new Agency role starts with it ticked (A2). It
     * never carries the key's Financials child: mspplatform omits `employee_metrics_financial` from
     * the Agency catalogue, and the server never computes the Financial card for an agency viewer
     * (A4). The lenient lookup answers a key it cannot find from the `users_roles` aliases instead,
     * which grants on a tree without it: a Company role, or an Agency role with no Employees tab.
     * strictSubmodule answers only from an `employee_metrics` node, or denies.
     *
     * The tree the guard reads is the one for the branch picked in the header switcher:
     * SelectAgencyMenu rebuilds backendRolePermissions with that branch's per-user override. The
     * page sends that branch as `branchId`, and the server checks the key again for it (A3).
     *
     * Platform-category viewers are held to the key here too. Release-1 removed the isPlatform
     * bypass in AppRoutes: an absent node (undefined) still lets a Platform role through, but strict
     * mode answers an absent `employee_metrics` with an explicit false, which denies every category.
     * The page's own gate stays as a second check.
     *
     * The guard stays synchronous, and reads the one client tree. The page then asks the server
     * (GET /api/team/staffer/access, useStafferAccess; tracker §20.12), which runs its own gate's
     * admission: every role held, every ancestor (a branch with Employees unticked), the branch.
     * When it says no, the page refuses with 'You don’t have access to Employee Metrics for this
     * branch.' instead of opening on reads that all 403 ('Couldn’t load').
     */
    id: 'users-roles-staffer-metrics',
    path: 'users-roles/employees/:userId/metrics',
    component: 'StafferMetricsPage',
    moduleKey: 'users_roles',
    submoduleKey: 'employee_metrics',
    strictSubmodule: true,
    variants: ['platform', 'msp', 'main-agency', 'agency'],
  },
  {
    id: 'users-roles-add-role',
    path: 'users-roles/roles/add',
    component: 'AddRolePage',
    moduleKey: 'users_roles',
    submoduleKey: 'roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    id: 'users-roles-edit-role',
    path: 'users-roles/roles/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'users_roles',
    submoduleKey: 'roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  {
    // Handles both detail view and users view via ?view=users query param
    id: 'users-roles-role-detail',
    path: 'users-roles/roles/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'users_roles',
    submoduleKey: 'roles',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company'],
  },
  // Global: Roles and Permissions page (category tabs: Platform/MSP/Agency/Company)
  // Note: general-admin has 'roles_permissions' as submodule of 'global', not 'users_roles'
  // The hasModulePermission function now searches submodules across all modules
  {
    id: 'roles-permissions',
    path: 'roles-permissions',
    component: 'GlobalRolesPermissionsPage',
    moduleKey: 'roles_permissions', // Changed to work with both users_roles.roles and global.roles_permissions
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },
  // Add/Edit role from Global Roles page
  {
    id: 'roles-permissions-add',
    path: 'roles-permissions/add',
    component: 'AddRolePage',
    moduleKey: 'roles_permissions',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },
  {
    id: 'roles-permissions-edit',
    path: 'roles-permissions/:roleId/edit',
    component: 'EditRolePage',
    moduleKey: 'roles_permissions',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },
  {
    id: 'roles-permissions-detail',
    path: 'roles-permissions/:roleId',
    component: 'RoleDetailPage',
    moduleKey: 'roles_permissions',
    variants: ['platform', 'msp', 'main-agency', 'agency', 'company', 'general-admin'],
  },

  // ============================================================================
  // GLOBAL ROUTES (Platform only)
  // ============================================================================
  {
    id: 'global-compensation-codes',
    path: 'global/compensation-codes',
    component: 'CompensationCodesPage',
    moduleKey: 'global',
    submoduleKey: 'compensation_codes',
    variants: ['platform'],
  },
  {
    id: 'global-wc-catalogs',
    path: 'global/wc-catalogs',
    component: 'WCCatalogsPage',
    moduleKey: 'global',
    submoduleKey: 'wc_catalogs',
    variants: ['platform'],
  },
  {
    id: 'global-wc-catalog-codes',
    path: 'global/wc-catalogs/compensation-codes/:catalogId/:tab?',
    component: 'CatalogCompensationCodesPage',
    moduleKey: 'global',
    submoduleKey: 'wc_catalogs',
    variants: ['platform'],
  },
  {
    id: 'global-paycodes',
    path: 'global/paycodes',
    component: 'PaycodesPage',
    moduleKey: 'global',
    submoduleKey: 'paycodes',
    variants: ['platform'],
  },
  {
    id: 'global-paycode-create',
    path: 'global/paycodes/create',
    component: 'PaycodeFormPage',
    moduleKey: 'global',
    submoduleKey: 'paycodes',
    variants: ['platform'],
  },
  {
    id: 'global-paycode-edit',
    path: 'global/paycodes/edit/:paycodeId',
    component: 'PaycodeFormPage',
    moduleKey: 'global',
    submoduleKey: 'paycodes',
    variants: ['platform'],
  },
  {
    id: 'global-authorities',
    path: 'global/authorities',
    component: 'AuthoritiesPage',
    moduleKey: 'global',
    submoduleKey: 'authorities',
    variants: ['platform'],
  },
  {
    id: 'global-tax',
    path: 'global/tax',
    component: 'TaxManagementPage',
    moduleKey: 'global',
    submoduleKey: 'tax',
    variants: ['platform'],
  },
  {
    id: 'global-metrics',
    path: 'global/metrics',
    component: 'MetricsDashboardPage',
    moduleKey: 'global',
    submoduleKey: 'metrics',
    variants: ['platform'],
  },
  {
    id: 'global-activity-log',
    path: 'global/activity-log',
    component: 'ActivityLogsPage',
    moduleKey: 'global',
    submoduleKey: 'activity_logs',
    variants: ['platform'],
  },
  {
    id: 'global-lenders',
    path: 'global/lenders',
    component: 'LenderAdminPage',
    moduleKey: 'global',
    submoduleKey: 'lender_financing',
    variants: ['platform'],
  },
  {
    id: 'global-financing-schedules',
    path: 'global/financing-schedules',
    component: 'FinancingSchedulesPage',
    moduleKey: 'global',
    submoduleKey: 'financing_schedules',
    variants: ['platform'],
    meta: {title: 'Financing Schedules'},
  },
  {
    id: 'global-financing-schedule-detail',
    path: 'global/financing-schedules/:scheduleId',
    component: 'FinancingSchedulesPage',
    moduleKey: 'global',
    submoduleKey: 'financing_schedules',
    variants: ['platform'],
    meta: {title: 'Financing Schedule Detail'},
  },
  {
    id: 'global-time-off',
    path: 'global/time-off',
    component: 'TimeOffPoliciesPage',
    moduleKey: 'global',
    submoduleKey: 'time_off',
    variants: ['platform'],
    meta: {title: 'Time Off Policies'},
  },
  {
    id: 'global-time-off-category',
    path: 'global/time-off/categories/:categoryId',
    component: 'TimeOffCategoryDetailPage',
    moduleKey: 'global',
    submoduleKey: 'time_off',
    variants: ['platform'],
    meta: {title: 'Time Off Category'},
  },
  {
    id: 'global-time-off-create-policy',
    path: 'global/time-off/policies/new',
    component: 'CreateTimeOffPolicyPage',
    moduleKey: 'global',
    submoduleKey: 'time_off',
    variants: ['platform'],
    meta: {title: 'Create Time Off Policy'},
  },
  {
    id: 'global-time-off-edit-policy',
    path: 'global/time-off/policies/:policyId/edit',
    component: 'CreateTimeOffPolicyPage',
    moduleKey: 'global',
    submoduleKey: 'time_off',
    variants: ['platform'],
    meta: {title: 'Edit Time Off Policy'},
  },
  {
    id: 'global-time-off-policy-detail',
    path: 'global/time-off/policies/:policyId',
    component: 'TimeOffPolicyDetailPage',
    moduleKey: 'global',
    submoduleKey: 'time_off',
    variants: ['platform'],
    meta: {title: 'Time Off Policy Details'},
  },
  {
    id: 'global-integrations-zoom',
    path: 'global/integrations/zoom',
    component: 'ZoomConfigPage',
    moduleKey: 'settings',
    submoduleKey: 'zoom_config',
    variants: ['platform'],
  },
  // Zoom Setup is also available under Settings for all roles (except Company)
  {
    id: 'settings-zoom-setup',
    path: 'settings/zoom-setup',
    component: 'ZoomSetupSettingsPage',
    moduleKey: 'settings',
    submoduleKey: 'zoom_config',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
    meta: {title: 'Zoom Setup'},
  },
  // Main agency also gets some global routes
  {
    id: 'main-agency-wc-catalogs',
    path: 'global/wc-catalogs',
    component: 'WCCatalogsPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'catalogs',
    variants: ['main-agency'],
  },
  {
    id: 'main-agency-paycodes',
    path: 'global/paycodes',
    component: 'PaycodesPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'payroll_setup',
    variants: ['main-agency'],
  },

  // ============================================================================
  // PAYROLL ROUTES (Platform only)
  // ============================================================================
  {
    id: 'payroll',
    path: 'payroll',
    component: 'PayrollManagementPage',
    moduleKey: 'payroll',
    variants: ['platform'],
  },
  {
    id: 'payroll-reports',
    path: 'payroll/reports',
    component: 'PayrollReportsPage',
    moduleKey: 'payroll',
    submoduleKey: 'generate_report',
    variants: ['platform'],
  },
  {
    id: 'payroll-billing-settings',
    path: 'payroll/:eorId/billing-settings/:companyId',
    component: 'BillingSettingsPage',
    moduleKey: 'payroll',
    variants: ['platform'],
  },
  {
    id: 'payroll-eor',
    path: 'payroll/:eorId/:view?/:batchId?',
    component: 'PayrollManagementPage',
    moduleKey: 'payroll',
    variants: ['platform'],
  },

  // ============================================================================
  // FINANCE ROUTES (Platform only) — KRAF-763 GET PAID. Gated on the payroll module: the people
  // who run payroll are the people who reconcile the money it bills. The reconciliation API is
  // platform-admin only (PlatformAdminGuard), so the route is too.
  // ============================================================================
  {
    id: 'finance',
    path: 'finance',
    component: 'FinancePage',
    moduleKey: 'payroll',
    variants: ['platform'],
    platformAdminOnly: true,
    meta: {title: 'Finance'},
  },

  // ============================================================================
  // DATA ROUTES
  // ============================================================================
  {
    id: 'import',
    path: 'import',
    component: 'ImportWizardMain',
    variants: ['platform', 'main-agency'],
  },
  {
    id: 'data-migration',
    path: 'data-migration',
    component: 'DataMigrationPage',
    moduleKey: 'global',
    submoduleKey: 'data_migration',
    variants: ['platform', 'main-agency'],
  },
  {
    // STFFLG-4130: `msp` is here because the layout is picked from the role's category, and every
    // MSP-category role carries dashboard > data_cleanup. Without it the dashboard's clean-up cards
    // opened RouteNotFound for them.
    id: 'data-cleanup',
    path: 'data-cleanup/:cardId',
    component: 'MspDataCleanupMain',
    moduleKey: 'dashboard',
    submoduleKey: 'data_cleanup',
    variants: ['main-agency', 'msp', 'agency'],
  },

  // ============================================================================
  // COMPANY-SPECIFIC ROUTES
  // ============================================================================
  {
    id: 'company-positions',
    path: 'manage-positions',
    component: 'ManagePositions',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_positions',
    variants: ['company'],
  },
  {
    id: 'company-add-position',
    path: 'manage-positions/add-position',
    component: 'AddNewPositionPage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_positions',
    variants: ['company'],
  },
  {
    id: 'company-edit-position',
    path: 'manage-positions/edit-position/:orderId',
    component: 'EditPositionPage',
    moduleKey: 'company_manage_profile',
    submoduleKey: 'company_positions',
    variants: ['company'],
  },
  {
    id: 'company-templates',
    path: 'manage-templates',
    component: 'ManageTemplates',
    moduleKey: 'other_settings',
    variants: ['company'],
  },
  {
    id: 'company-invoices',
    path: 'invoices',
    component: 'InvoicesPage',
    moduleKey: 'company_invoices',
    variants: ['company'],
  },
  {
    id: 'company-timesheets',
    path: 'timesheets',
    component: 'TimesheetPendingView',
    moduleKey: 'company_timesheets',
    variants: ['company'],
  },
  {
    id: 'company-profile-redirect',
    path: 'manage-company',
    component: 'CompanyProfileRedirect',
    moduleKey: 'company_manage_profile',
    variants: ['company'],
  },
  {
    id: 'company-manage-profile',
    path: 'manage-profile',
    component: 'CompanyProfileRedirect',
    moduleKey: 'manage_profile',
    variants: ['company'],
  },

  // ============================================================================
  // MSP-SPECIFIC ROUTES
  // ============================================================================
  {
    id: 'msp-client-positions',
    path: 'client-positions',
    component: 'ManageClientPositions',
    moduleKey: 'manage_companies',
    submoduleKey: 'company_positions',
    variants: ['msp'],
  },
  {
    id: 'msp-templates',
    path: 'manage-templates',
    component: 'MspManageTemplates',
    variants: ['msp'],
  },
  // Platform admins have no "Manage Profile" item — they reach agencies via "Manage Agencies".
  // MSP layouts do have one; it uses the shared /manage-agency route below.

  // ============================================================================
  // MAIN AGENCY SPECIFIC ROUTES
  // ============================================================================
  {
    id: 'main-agency-managed-sites',
    path: 'managed-sites',
    component: 'ManagedSitesPage',
    moduleKey: 'manage_agencies',
    submoduleKey: 'managed_sites',
    variants: ['main-agency'],
  },
  {
    id: 'main-agency-manage-profile',
    path: 'manage-profile',
    component: 'AgencyProfileRedirect',
    moduleKey: 'manage_profile',
    variants: ['main-agency'],
  },
  {
    id: 'main-agency-manage-agency',
    path: 'manage-agency',
    component: 'AgencyProfileRedirect',
    moduleKey: 'manage_profile',
    // The "Manage Profile" nav item of every agency-side layout links here.
    variants: ['main-agency', 'agency', 'msp'],
  },

  // ============================================================================
  // SETTINGS ROUTES
  // ============================================================================
  {
    id: 'settings-event-categories',
    path: 'settings/event-categories',
    component: 'EventCategoriesSettingsPage',
    moduleKey: 'settings',
    submoduleKey: 'event_categories',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
    meta: {title: 'Event Categories Settings'},
  },
  {
    id: 'settings-compliance-fields',
    path: 'settings/compliance-fields',
    component: 'ComplianceFieldsSettingsPage',
    moduleKey: 'settings',
    submoduleKey: 'compliance_fields',
    variants: ['platform', 'main-agency', 'msp', 'agency'],
    meta: {title: 'Compliance Fields Settings'},
  },
  {
    id: 'settings-documents-files',
    path: 'settings/documents/files',
    component: 'SettingsESignFilesPage',
    // STFFLG-3958: eSign lives UNDER Settings (settings > esign > esign_documents),
    // matching where these screens actually sit in the nav. Two keys still reach it:
    // findKeyInModuleSubmodules searches direct submodules and then one level deeper,
    // so naming the leaf is enough — no third route key needed.
    moduleKey: 'settings',
    submoduleKey: 'esign_documents',
    variants: ['agency', 'main-agency', 'msp'],
    meta: {title: 'Document Files'},
  },
  {
    id: 'settings-documents-signatures',
    path: 'settings/documents/signatures',
    component: 'SettingsESignSignaturesPage',
    // STFFLG-3958: see settings-documents-files above — both admin screens share one
    // permission, because managing files and managing signature requests are one job.
    moduleKey: 'settings',
    submoduleKey: 'esign_documents',
    variants: ['agency', 'main-agency', 'msp'],
    meta: {title: 'Signatures'},
  },
  // STFFLG-4185: every Settings route names its row in the role matrix (submoduleKey), and
  // settingsPages.ts lists it; settingsPages.test.ts fails when either is missing. E-Verify and
  // Help used to be ungated, and FAQ Admin / Invoice Templates gated on the bare 'settings'
  // module, so no matrix row could switch them off.
  {
    id: 'settings-e-verify',
    path: 'settings/e-verify',
    component: 'EVerifySettingsPage',
    moduleKey: 'settings',
    submoduleKey: 'work_authorization',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
    meta: {title: 'E-Verify'},
  },
  {
    id: 'settings-help',
    path: 'settings/help',
    component: 'HelpSettingsPage',
    moduleKey: 'settings',
    submoduleKey: 'help_faq',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'company'],
    meta: {title: 'Help / FAQ'},
  },
  {
    id: 'settings-faq-admin',
    path: 'settings/faq-admin',
    component: 'FaqAdminSettingsPage',
    moduleKey: 'settings',
    submoduleKey: 'faq_admin',
    variants: ['platform'],
    meta: {title: 'FAQ Admin'},
  },
  {
    id: 'settings-invoice-templates',
    path: 'settings/invoice-templates',
    component: 'InvoiceTemplatesSettingsPage',
    moduleKey: 'settings',
    submoduleKey: 'invoice_templates',
    variants: ['platform'],
    meta: {title: 'Invoice Templates'},
  },
  {
    id: 'settings-redirect',
    path: 'settings',
    component: 'SettingsRedirect',
    meta: {title: 'Settings'},
  },

  // ============================================================================
  // ONBOARDING ROUTES (Platform only)
  // ============================================================================
  {
    id: 'onboarding-agency',
    path: 'onboarding-agency',
    component: 'OnboardingAgency',
    moduleKey: 'manage_agencies',
    variants: ['platform'],
  },
  {
    id: 'onboarding-company',
    path: 'onboarding-company',
    component: 'OnboardingCompany',
    moduleKey: 'manage_companies',
    submoduleKey: 'create_company',
    variants: ['platform'],
  },

  // ============================================================================
  // REPORTS ROUTES
  // ============================================================================
  {
    id: 'reports',
    path: 'reports',
    component: 'ReportsPage',
    moduleKey: 'reports',
    variants: ['platform', 'msp', 'main-agency', 'agency'],
    meta: {title: 'Reports'},
  },
  {
    id: 'reports-builder',
    path: 'reports/builder',
    component: 'ReportBuilderPage',
    moduleKey: 'reports',
    variants: ['platform', 'msp', 'main-agency', 'agency'],
    meta: {title: 'Custom Report Builder'},
  },

  // ============================================================================
  // ANNOUNCEMENT ROUTES
  // Static create/edit paths must remain before the generic detail path.
  // ============================================================================
  {
    id: 'announcement-create',
    path: 'announcements/new',
    component: 'AnnouncementFormPage',
    moduleKey: 'announcements',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'company'],
  },
  {
    id: 'announcement-edit',
    path: 'announcements/:id/edit',
    component: 'AnnouncementFormPage',
    moduleKey: 'announcements',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'company'],
  },
  {
    id: 'announcement-detail',
    path: 'announcements/:id',
    component: 'AnnouncementDetailPage',
    moduleKey: 'announcements',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'company'],
  },
  {
    id: 'announcements',
    path: 'announcements',
    component: 'AnnouncementListPage',
    moduleKey: 'announcements',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'company'],
  },

  // ============================================================================
  // INDEX/REDIRECT ROUTES
  // ============================================================================
  {
    id: 'platform-index',
    path: '',
    component: 'Navigate',
    variants: ['platform'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'main-agency-index',
    path: '',
    component: 'Navigate',
    variants: ['main-agency'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'msp-index',
    path: '',
    component: 'Navigate',
    variants: ['msp'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'agency-index',
    path: '',
    component: 'Navigate',
    variants: ['agency'],
    meta: {index: true, redirectTo: 'manage-companies'},
  },
  {
    id: 'company-index',
    path: '',
    component: 'Navigate',
    variants: ['company'],
    meta: {index: true, redirectTo: 'manage-orders'},
  },

  // ============================================================================
  // ESIGN / DOCUMENTS ROUTES (agency and msp layouts, plus the candidate signing flow)
  // The signing flow and my-signatures pages are accessible to any user
  // who has been assigned as a signer on a document.
  // Files and Signatures management moved to Settings (settings/documents/files, settings/documents/signatures).
  // ============================================================================
  {
    id: 'signing-flow',
    path: 'signing/:requestId',
    component: 'SigningView',
    // STFFLG-3958: deliberately NO moduleKey. A signer is authorized by being invited
    // (participant-email match server-side, see Bug 3328 in esign.controller.ts), not by
    // holding a permission -- that is what lets EOR contacts and named reps sign. Adding
    // a guard here would lock out exactly those people.
    //
    // STFFLG-4130: `msp` because the EOR co-signer is the parent agency's admin, and an
    // MSP-category login gets the msp layout; the notification bell sent them to RouteNotFound.
    variants: ['agency', 'msp', 'candidate'],
    meta: {title: 'Sign Document'},
  },
  {
    id: 'my-signatures',
    path: 'my-signatures',
    component: 'MySignaturesPage',
    // STFFLG-3958: the signer's own surface, gated on Signing rather than Documents --
    // holding it confers no administrative access. NOTE the signing route itself stays
    // ungated on purpose (see below).
    //
    // STFFLG-4130: `msp` keeps the path in the msp layout's table, but MSP roles cannot hold
    // Signing (it is not in their catalogue), so the guard sends them to /unauthorized.
    // SigningView's Done reads the same key and takes such a signer home instead of here.
    moduleKey: 'settings',
    submoduleKey: 'esign_signing',
    variants: ['agency', 'msp'],
    meta: {title: 'My Signatures'},
  },
  {
    id: 'candidate-my-signatures-redirect',
    path: 'my-signatures',
    component: 'Navigate',
    variants: ['candidate'],
    // Deep-link straight to the Signed Documents tab. The post-signing "Done" button sends a
    // candidate to /my-signatures, and without the tab they would land on the Documents default
    // tab instead of the signed copy they just produced.
    meta: {title: 'My Signatures', redirectTo: '/documents?tab=signDocs'},
  },

  // ============================================================================
  // NOTIFICATIONS — the inbox, and the legacy /tasks redirect to it
  //
  // STFFLG-3995: redirectTo on a route that has its OWN path must be ABSOLUTE. React Router
  // resolves a relative target against that route's own pathnameBase, so 'settings/e-verify' on
  // the 'tasks' route becomes /tasks/settings/e-verify — a path nothing matches. Index routes
  // (index: true, path '') are the exception: their base is the parent, so a relative target is
  // correct there, which is why the other redirects in this file look different and are fine.
  // ============================================================================
  {
    // STFFLG-4185: every notification type, the bell's "View all". Deliberately NO moduleKey: it is
    // the user's own feed, which the bell already shows them, so it must not hang off a Settings
    // row (Settings > Work Authorization lists the E-Verify tasks only). Variants = every layout
    // that renders NotificationBell (NavMenuBar: all but company).
    id: 'notifications-inbox',
    path: 'notifications',
    component: 'NotificationsPage',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin', 'candidate'],
    meta: {title: 'Notifications'},
  },
  {
    // /tasks was the inbox before it moved under Settings; old links and bookmarks now reach the
    // inbox, which every bell user can open, rather than a Settings page they may not hold.
    id: 'tasks-redirect',
    path: 'tasks',
    component: 'Navigate',
    variants: ['platform', 'main-agency', 'msp', 'agency', 'general-admin'],
    meta: {title: 'Notifications', redirectTo: '/notifications'},
  },

  // ============================================================================
  // CANDIDATE SELF-SERVICE ROUTES
  // ============================================================================
  {
    id: 'candidate-index',
    path: '',
    component: 'Navigate',
    variants: ['candidate'],
    meta: {index: true, redirectTo: 'dashboard'},
  },
  {
    id: 'candidate-dashboard',
    path: 'dashboard',
    component: 'CandidateDashboard',
    moduleKey: 'candidate_dashboard',
    variants: ['candidate'],
  },
  {
    id: 'candidate-announcement-detail',
    path: 'announcements/:id',
    component: 'CandidateAnnouncementDetailPage',
    moduleKey: 'candidate_dashboard',
    variants: ['candidate'],
  },
  {
    id: 'candidate-announcements',
    path: 'announcements',
    component: 'CandidateAnnouncementsPage',
    moduleKey: 'candidate_dashboard',
    variants: ['candidate'],
  },
  {
    id: 'candidate-profile',
    path: 'my-profile',
    component: 'CandidateProfile',
    moduleKey: 'candidate_profile',
    variants: ['candidate'],
  },
  {
    id: 'candidate-paychecks',
    path: 'paychecks',
    component: 'CandidatePaychecks',
    moduleKey: 'paychecks',
    variants: ['candidate'],
  },
  {
    id: 'candidate-documents',
    path: 'documents',
    component: 'CandidateDocuments',
    moduleKey: 'candidate_documents',
    variants: ['candidate'],
  },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Whether a route or menu entry marked `platformAdminOnly` may be shown to this user. Unmarked
 * entries always pass; marked ones need isPlatform === true (unknown permissions do not pass).
 */
export const passesPlatformAdminGate = (
  entry: {platformAdminOnly?: boolean},
  isPlatform: boolean | undefined,
): boolean => !entry.platformAdminOnly || isPlatform === true

/**
 * Gets routes for a specific layout variant
 * Filters ALL_ROUTES based on variant
 */
export const getRoutesForVariant = (variant: LayoutVariant): RouteConfig[] => {
  return ALL_ROUTES.filter((route) => {
    // TODO(feature-flag): dashboardMetrics — remove filter when flag is deleted
    if (route.id === 'platform-dashboard' && !isEnabled('dashboardMetrics')) {
      return false
    }

    // TODO(feature-flag): agencyTimeOffRequests — remove filter when flag is deleted
    if (route.id === 'requests' && !isEnabled('agencyTimeOffRequests')) {
      return false
    }

    // TODO(feature-flag): timeOffPolicies — remove filter when flag is deleted
    const timeOffRouteIds = [
      'global-time-off',
      'global-time-off-category',
      'global-time-off-create-policy',
      'global-time-off-edit-policy',
      'global-time-off-policy-detail',
    ]
    if (timeOffRouteIds.includes(route.id) && !isEnabled('timeOffPolicies')) {
      return false
    }

    // If no variants specified, route is available to all
    if (!route.variants) return true
    // Otherwise, check if variant is in the allowed list
    return route.variants.includes(variant)
  })
}

/**
 * Gets layout configuration for a variant
 */
export const getLayoutConfig = (variant: LayoutVariant): LayoutConfig => {
  const basePaths: Record<LayoutVariant, string> = {
    platform: '/platform-admin',
    'general-admin': '/general-admin',
    'main-agency': '/main-agency-admin',
    msp: '/msp-admin',
    agency: '/agency',
    company: '/company-admin',
    candidate: '/candidate',
  }

  return {
    variant,
    basePath: basePaths[variant],
    routes: getRoutesForVariant(variant),
  }
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility during migration)
// ============================================================================

export const PLATFORM_ADMIN_CONFIG = getLayoutConfig('platform')
export const MAIN_AGENCY_ADMIN_CONFIG = getLayoutConfig('main-agency')
export const MSP_AGENCY_ADMIN_CONFIG = getLayoutConfig('msp')
export const AGENCY_ADMIN_CONFIG = getLayoutConfig('agency')
export const COMPANY_ADMIN_CONFIG = getLayoutConfig('company')
export const CANDIDATE_CONFIG = getLayoutConfig('candidate')

export const ALL_LAYOUT_CONFIGS: LayoutConfig[] = [
  PLATFORM_ADMIN_CONFIG,
  MAIN_AGENCY_ADMIN_CONFIG,
  MSP_AGENCY_ADMIN_CONFIG,
  AGENCY_ADMIN_CONFIG,
  COMPANY_ADMIN_CONFIG,
  CANDIDATE_CONFIG,
]

/**
 * Gets layout config by base path
 */
export const getLayoutConfigByPath = (path: string): LayoutConfig | undefined => {
  return ALL_LAYOUT_CONFIGS.find((config) => path.startsWith(config.basePath))
}
